import React, { useState } from 'react';
import { 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Button,
  Box,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  WarningAmber as WarningIcon,
  Storage as StorageIcon,
  Sync as SyncIcon,
  Analytics as AnalyticsIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { checkDataHygiene, getHygieneIssues } from '../services/api';
import { HygieneSummary, HygieneIssue } from '../types';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState<React.ReactNode | null>(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);

  const handleCheckSfdcAffinity = async () => {
    try {
      setLoading(true);
      setError(null);
      setDialogTitle('SFDC & Affinity Data Inconsistencies');
      setOpenDialog(true);
      
      // Get hygiene issues for SFDC and Affinity
      const issues = await getHygieneIssues('all', 'all');
      const filteredIssues = issues.filter((issue: HygieneIssue) => 
        issue.related_id && (issue.related_id.includes('sfdc') || issue.related_id.includes('affinity'))
      );
      
      if (filteredIssues.length === 0) {
        setDialogContent(
          <Alert severity="success" sx={{ mt: 2 }}>
            No inconsistencies found between SFDC and Affinity data.
          </Alert>
        );
      } else {
        setDialogContent(
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Found {filteredIssues.length} inconsistencies between SFDC and Affinity data.
            </Alert>
            <List>
              {filteredIssues.slice(0, 10).map((issue: HygieneIssue, index: number) => (
                <React.Fragment key={issue.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={`Issue in ${issue.field}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {issue.issue}
                          </Typography>
                          {` — ${issue.recommendation}`}
                        </>
                      }
                    />
                  </ListItem>
                  {index < filteredIssues.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {filteredIssues.length > 10 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Showing 10 of {filteredIssues.length} issues. View Reports for complete analysis.
              </Typography>
            )}
          </>
        );
      }
    } catch (err) {
      setError('Failed to check data inconsistencies. Please try again later.');
      setDialogContent(
        <Alert severity="error">
          Failed to check data inconsistencies. Please try again later.
        </Alert>
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDwh = async () => {
    try {
      setLoading(true);
      setError(null);
      setDialogTitle('Data Warehouse Inconsistencies');
      setOpenDialog(true);
      
      // Get hygiene issues for DWH
      const issues = await getHygieneIssues('dwh', 'all');
      
      if (issues.length === 0) {
        setDialogContent(
          <Alert severity="success" sx={{ mt: 2 }}>
            No inconsistencies found in the Data Warehouse.
          </Alert>
        );
      } else {
        setDialogContent(
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Found {issues.length} inconsistencies in the Data Warehouse.
            </Alert>
            <List>
              {issues.slice(0, 10).map((issue: HygieneIssue, index: number) => (
                <React.Fragment key={issue.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={`Issue in ${issue.field}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {issue.issue}
                          </Typography>
                          {` — ${issue.recommendation}`}
                        </>
                      }
                    />
                  </ListItem>
                  {index < issues.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {issues.length > 10 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                Showing 10 of {issues.length} issues. Go to Data Warehouse for complete analysis.
              </Typography>
            )}
          </>
        );
      }
    } catch (err) {
      setError('Failed to check data warehouse inconsistencies. Please try again later.');
      setDialogContent(
        <Alert severity="error">
          Failed to check data warehouse inconsistencies. Please try again later.
        </Alert>
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePopulateDwh = () => {
    setSyncInProgress(true);
    setDialogTitle('Populating Data Warehouse');
    setDialogContent(
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>
          Extracting and transforming data from source systems...
        </Typography>
      </Box>
    );
    setOpenDialog(true);
    
    // Simulate ETL process
    setTimeout(() => {
      setSyncInProgress(false);
      setSyncComplete(true);
      setDialogContent(
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Data Warehouse Successfully Populated
          </Typography>
          <Typography>
            All data has been extracted, transformed, and loaded into your data warehouse.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={() => {
              setOpenDialog(false);
              navigate('/dwh');
            }}
          >
            View Data Warehouse
          </Button>
        </Box>
      );
    }, 3000);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSyncInProgress(false);
    setSyncComplete(false);
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Data Cleaning & Analysis Dashboard
      </Typography>
      
      <Typography variant="body1" paragraph>
        Welcome to the OpenAna Data Cleaning & Analysis platform. This dashboard provides tools to analyze data from different sources, 
        check data hygiene, and suggest a unified data model for your central data warehouse.
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
        {/* Data Sources */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader title="Data Sources" />
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6">Affinity CRM</Typography>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="body2">
                        View and analyze data from Affinity CRM including contacts, companies, and interactions.
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => navigate('/affinity')}
                    >
                      View Affinity Data
                    </Button>
                  </Paper>
                </Box>
                <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6">Salesforce</Typography>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="body2">
                        View and analyze data from Salesforce including contacts, accounts, and opportunities.
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => navigate('/sfdc')}
                    >
                      View SFDC Data
                    </Button>
                  </Paper>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        {/* Data Warehouse */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader 
              title="Data Warehouse" 
              avatar={<StorageIcon color="primary" />}
            />
            <CardContent>
              <Typography variant="body1" paragraph>
                View and manage your central data warehouse. Analyze the unified data model and ensure data consistency.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/dwh')}
              >
                Manage Data Warehouse
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        {/* Data Hygiene */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader 
              title="Data Hygiene" 
              avatar={<WarningIcon color="warning" />}
            />
            <CardContent>
              <Typography variant="body1" paragraph>
                Check for data inconsistencies and issues across your data sources. Get recommendations for data cleaning.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  sx={{ flex: 1 }}
                  onClick={handleCheckSfdcAffinity}
                  disabled={loading}
                >
                  Check SFDC & Affinity
                </Button>
                <Button 
                  variant="outlined" 
                  color="warning" 
                  sx={{ flex: 1 }}
                  onClick={handleCheckDwh}
                  disabled={loading}
                >
                  Check Data Warehouse
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        
        {/* Data Synchronization */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader 
              title="Data Synchronization" 
              avatar={<SyncIcon color="info" />}
            />
            <CardContent>
              <Typography variant="body1" paragraph>
                Populate your data warehouse with clean, consistent data from your source systems.
              </Typography>
              <Button 
                variant="contained" 
                color="info"
                onClick={handlePopulateDwh}
                disabled={syncInProgress}
              >
                Populate Data Warehouse
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        {/* Reports & Analytics */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader 
              title="Reports & Analytics" 
              avatar={<AnalyticsIcon color="success" />}
            />
            <CardContent>
              <Typography variant="body1" paragraph>
                Generate reports and analytics based on your unified data. Get insights into your business data.
              </Typography>
              <Button 
                variant="contained" 
                color="success" 
                onClick={() => navigate('/reports')}
              >
                View Reports & Analytics
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        {/* Data Q&A */}
        <Box sx={{ flex: '1 1 45%', minWidth: 300 }}>
          <Card>
            <CardHeader 
              title="Data Q&A" 
              avatar={<ChatIcon color="secondary" />}
            />
            <CardContent>
              <Typography variant="body1" paragraph>
                Ask questions about your data in natural language. Get insights through conversation.
              </Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={() => navigate('/qa')}
              >
                Converse with Data
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      {/* Dialog for displaying results */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            dialogContent
          )}
        </DialogContent>
        <DialogActions>
          {!syncInProgress && (
            <Button onClick={handleCloseDialog} color="primary" autoFocus>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
